import axios from "axios";
import moment from "moment-with-locales-es6";
import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import { DOCUMENT_TYPES, getDriverInfo } from "../../services/GetDriverInfo";

import { removeAccents } from "../../utils/string-utils";
import { useOnInit } from "../../utils/useOnInit";
import { DomainContext } from "../../domain-repository/DomainRepository";
import { driverInfoReducer } from "./driverInfoReducer";
import {
  checkDriverInfoStepEditMode,
  DriverFieldsErrors,
  filterCountries,
  isPortugalCountry,
  mapCountryToOption,
  useFocusChain,
  validValidationObject,
} from "./driverInfoStepUtils";
import {
  validateCountry,
  validateDriversLicense,
  validateDriversLicenseExpirationDate,
  validateDriversLicenseIssuedDate,
  validateNotEmpty,
} from "./driver-fields-validations";
import { isPortugalCountryId, padZero, PORTUGAL_COUNTRY_ID } from "../../utils/utils";

import DriverInfoDropdown from "./components/DriverInfoDropdown";
import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import Loading from "../../components/Loading";
import TrackingPage from "../../components/TrackingPage";
import withLang from "../../withLang";
import { DriverInfoPage } from "./components/DriverInfoPage";
import { LabeledDriverInfoInput } from "./components/DriverInfoInput";
import { Spacer } from "../../components/Spacer";
import DriverInfoOptionsInput from "./components/DriverInfoOptionsInput";
import DriverDateInput from "./components/DriverDateInput";
import alertModalManager from '../../components/alert-modal-wraper/alert-modal-manager'
import { AlertModalWithActions } from "../../components/alert-modal/AlertModal";

export function getDriversCountryIdByName(countries, name) {
  const country = countries.find((c) => c.name === name);
  return country ? country.countryID : name;
}

function getDriversCountryNameById(countries, id) {
  const country = countries.find((c) => c.countryID === id);
  return country ? country.name : id;
}

const FIELD_ORDER = [
  "fullName",
  "birthDay",
  "birthMonth",
  "birthYear",
  "driversLicenseIssuedCountry",
  "driversLicense",
  "driversLicenseIssuedDay",
  "driversLicenseIssuedMonth",
  "driversLicenseIssuedYear",
  "driversLicenseExpirationDay",
  "driversLicenseExpirationMonth",
  "driversLicenseExpirationYear",
];

function AdditionalDriverStepPage(props) {
  const { t, i18n } = useTranslation();
  const { registerRef, focusNext } = useFocusChain(FIELD_ORDER);

  const {
    additionalDriverInfo,
    setDriverInfo,
    driverInfoHasClickedBack,
    setDriverInfoHasClickedBack,
    driverInfoEditMode,
    setDriverInfoEditMode,
    reservation,
    flowPreviousRoute,
    visitedRoutes,
    addVisitedRoutes,
    getDriversMinimumAgeForReservation,
    addAdditionalDriverInfo,
    getCountries,
    invalidBirthdates,
    addAdditionalDriver,
    hasAdditionalDriver,
    groupsDetails,
  } = useContext(DomainContext);
  const safeReservation = reservation || {};

  const vehicleGroupId = safeReservation.GroupID;
  const groupInfo = groupsDetails.find(g => g.id === vehicleGroupId) || {};

  const countries = useMemo(() => {
    const cs = getCountries();
    const ptCountry = cs.find((o) => o.countryID === PORTUGAL_COUNTRY_ID);
    return [ptCountry, ...cs.filter((o) => o.countryID !== PORTUGAL_COUNTRY_ID)].filter(
      (o) => o !== undefined
    );
  }, [getCountries]);

  const [state, dispatch] = useReducer(driverInfoReducer, {
    validatingCanSkipPage: true,
    canDisplayErrors: false,

    fullName: additionalDriverInfo.fullName || "",
    birthDay:
      (additionalDriverInfo.birthDate &&
        moment(additionalDriverInfo.birthDate, "YYYY-MM-DD").date()) ||
      "",
    birthMonth:
      (additionalDriverInfo.birthDate &&
        moment(additionalDriverInfo.birthDate, "YYYY-MM-DD").month() + 1) ||
      "",
    birthYear:
      (additionalDriverInfo.birthDate &&
        moment(additionalDriverInfo.birthDate, "YYYY-MM-DD").year()) ||
      "",

    driversLicenseIssuedPlace: additionalDriverInfo.driversLicenseIssuedPlace
      ? {
          value: getDriversCountryIdByName(
            countries,
            additionalDriverInfo.driversLicenseIssuedPlace
          ),
          label: additionalDriverInfo.driversLicenseIssuedPlace,
        }
      : null,    
    driversLicense: additionalDriverInfo.driversLicense,
    driversLicenseIssuedDay:
      (additionalDriverInfo.driversLicenseIssuedDate &&
        moment(additionalDriverInfo.driversLicenseIssuedDate).date()) ||
      "",
    driversLicenseIssuedMonth:
      (additionalDriverInfo.driversLicenseIssuedDate &&
        moment(additionalDriverInfo.driversLicenseIssuedDate).month() + 1) ||
      "",
    driversLicenseIssuedYear:
      (additionalDriverInfo.driversLicenseIssuedDate &&
        moment(additionalDriverInfo.driversLicenseIssuedDate).year()) ||
      "",

    driversLicenseExpirationDay:
      (additionalDriverInfo.driversLicenseExpirationDate &&
        moment(additionalDriverInfo.driversLicenseExpirationDate).date()) ||
      "",
    driversLicenseExpirationMonth:
      (additionalDriverInfo.driversLicenseExpirationDate &&
        moment(additionalDriverInfo.driversLicenseExpirationDate).month() +
          1) ||
      "",
    driversLicenseExpirationYear:
      (additionalDriverInfo.driversLicenseExpirationDate &&
        moment(additionalDriverInfo.driversLicenseExpirationDate).year()) ||
      "",

    minimumLicencePopupInfo: {
      isVisible: false,
      message: '',
      title: ''
    },

    isLoading: null,
  });

  const countriesOptions = useMemo(() => {
    return countries.map(mapCountryToOption);
  }, [countries]);

  const isLicencePT = useMemo(
    () => isPortugalCountry(state.driversLicenseIssuedPlace?.label),
    [state.driversLicenseIssuedPlace]
  );

  const expireDateOptional = useMemo(() => {
    return (
      reservation.configurations?.driversLicenseExpirationDateCountries?.includes(
        getDriversCountryIdByName(
          countries,
          state.driversLicenseIssuedPlace?.label
        )
      ) || false
    );
  }, [
    reservation.configurations,
    countries,
    state.driversLicenseIssuedPlace?.label,
  ]);

  const issuedDateOptional = useMemo(() => {
    return (
      reservation.configurations?.driversLicenseEmissionDateCountries?.includes(
        getDriversCountryIdByName(countries, state.driversLicenseIssuedCountry)
      ) || false
    );
  }, [reservation.configurations, countries, state.driversLicenseIssuedCountry]);

  const fullNameValidation = useMemo(() => {
    return validateNotEmpty(t, state.fullName, "emptyFullName");
  }, [t, state.fullName]);

  const birthDateValidation = useMemo(() => {
    function validateBirthDate() {
      if (
        state.birthDay &&
        state.birthMonth &&
        state.birthYear &&
        Number.isInteger(Number(state.birthDay)) &&
        Number.isInteger(Number(state.birthMonth)) &&
        Number.isInteger(Number(state.birthYear))
      ) {
        const birthDate = `${state.birthYear}-${padZero(
          state.birthMonth
        )}-${padZero(state.birthDay)}`;
        if (!moment(birthDate, "YYYY-MM-DD").isValid()) {
          return { isValid: false, message: t("invalidBirthDate") };
        }
        if (moment().diff(birthDate, "years") > 80) {
          return { isValid: false, message: t("tooOldToDrive") };
        }
        if (
          moment().diff(birthDate, "years") <
          getDriversMinimumAgeForReservation()
        ) {
          return { isValid: false, message: t("tooYoungToDrive") };
        }
        if (invalidBirthdates.some((b) => b === birthDate)) {
          return { isValid: false, message: t("invalidBirthDate") };
        }
        return { isValid: true };
      }
      return { isValid: false, message: t("invalidBirthDate") };
    }
    return validateBirthDate();
  }, [state.birthDay, state.birthMonth, state.birthYear, invalidBirthdates, t]);

  const driversLicenseValidation = useMemo(
    () => validateDriversLicense(t, state.driversLicense, isLicencePT),
    [state.driversLicense, isLicencePT, t]
  );

  const driversLicenseIssuedCountryValidation = useMemo(
    () => validateCountry(t, state.driversLicenseIssuedPlace?.label, countries),
    [countries, state.driversLicenseIssuedPlace, t]
  );

  const driversLicenseIssuedDateValidation = useMemo(
    () =>
      validateDriversLicenseIssuedDate(
        t,
        state.driversLicenseIssuedDay,
        state.driversLicenseIssuedMonth,
        state.driversLicenseIssuedYear,
        issuedDateOptional
      ),
    [
      state.driversLicenseIssuedDay,
      state.driversLicenseIssuedMonth,
      state.driversLicenseIssuedYear,
      t,
      issuedDateOptional,
    ]
  );

  const driversLicenseExpirationDateValidation = useMemo(
    () =>
      validateDriversLicenseExpirationDate(
        t,
        state.driversLicenseExpirationDay,
        state.driversLicenseExpirationMonth,
        state.driversLicenseExpirationYear,
        expireDateOptional,
        state.driversLicenseIssuedDay,
        state.driversLicenseIssuedMonth,
        state.driversLicenseIssuedYear,
        issuedDateOptional,
        driversLicenseIssuedDateValidation.isValid
      ),
    [
      state.driversLicenseExpirationDay,
      state.driversLicenseExpirationMonth,
      state.driversLicenseExpirationYear,
      state.driversLicenseIssuedDay,
      state.driversLicenseIssuedMonth,
      state.driversLicenseIssuedYear,
      expireDateOptional,
      issuedDateOptional,
      driversLicenseIssuedDateValidation.isValid,
      t,
    ]
  );

  const canNext = useCallback(() => {
    return (
      fullNameValidation.isValid &&
      birthDateValidation.isValid &&
      driversLicenseIssuedCountryValidation.isValid &&
      driversLicenseValidation.isValid &&
      driversLicenseIssuedDateValidation.isValid &&
      driversLicenseExpirationDateValidation.isValid
    );
  }, [
    fullNameValidation,
    birthDateValidation,
    driversLicenseIssuedCountryValidation,
    driversLicenseValidation,
    driversLicenseIssuedDateValidation,
    driversLicenseExpirationDateValidation,
  ]);

  const handleProceedNextPage = useCallback((type, skipIssuedDateValidation) => {
    const issuedDateEmptyValid = issuedDateOptional && !state.driversLicenseIssuedDay && !state.driversLicenseIssuedMonth && !state.driversLicenseIssuedYear
    if (!skipIssuedDateValidation && issuedDateEmptyValid) {
      dispatch({
        type: 'SET_VALUE',
        payload: {
          minimumLicencePopupInfo: {
            isVisible: true,
            title: t('minimumYearsLicenceTitle', { minimumLicenceTime: groupInfo.minimumLicenceTime }),
            message: ''
          }
        }
      });
      return null;
    }

    addAdditionalDriverInfo({
      fullName: state.fullName,
      birthDate: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
      driversLicense: state.driversLicense,
      driversLicenseIssuedPlace: state.driversLicenseIssuedPlace.label,
      driversLicenseIssuedDate: state.driversLicenseIssuedYear
       ? `${state.driversLicenseIssuedYear}-${state.driversLicenseIssuedMonth}-${state.driversLicenseIssuedDay}` : undefined,
      driversLicenseExpirationDate: state.driversLicenseExpirationYear
        ? `${state.driversLicenseExpirationYear}-${state.driversLicenseExpirationMonth}-${state.driversLicenseExpirationDay}`
        : undefined,
    });

    if (!hasAdditionalDriver()) {
      addAdditionalDriver()
    }
    
    props.history.push({
      pathname: props.history.location.state?.reviewUrl || "/driver-info-review",
      state: props.history.location.state,
    });
  }, [
    state.fullName,
    state.birthYear,
    state.birthMonth,
    state.birthDay,
    state.driversLicenseIssuedPlace,
    state.driversLicenseIssuedYear,
    state.driversLicenseIssuedMonth,
    state.driversLicenseIssuedDay,
    state.driversLicenseExpirationYear,
    state.driversLicenseExpirationMonth,
    state.driversLicenseExpirationDay,
    state.driversLicense,
    issuedDateOptional,
  ]);

  useEffect(() => {
    const editMode = checkDriverInfoStepEditMode(props.location.search);
    setDriverInfoEditMode(editMode);
  }, []);

  const onNextClick = useCallback(
    (type) => {
      const canSkipPage = canNext();
      dispatch({ type: "SET_VALUES", payload: { canDisplayErrors: true } });
      if (canSkipPage) {
        handleProceedNextPage(type);
      }
    },
    [handleProceedNextPage, canNext]
  );

  const onBack = useCallback(() => {
    props.history.goBack();
  }, []);

  const [validateCanSkip, setValidateCanSkip] = useState(
    !visitedRoutes.includes(window.location.pathname)
  );
  useOnInit(() => {
    const editMode = checkDriverInfoStepEditMode(props.location.search);
    if (!editMode && !visitedRoutes.includes(window.location.pathname)) {
      const canSkipPage = canNext();
      if (canSkipPage) {
        handleProceedNextPage("redirect");
      } else {
        addVisitedRoutes(window.location.pathname);
      }
    }
    setValidateCanSkip(false);
  });

  function onMinimumLicenYearsPopupPositiveClick() {
    dispatch({ type: 'SET_VALUE', payload: { minimumLicencePopupInfo: { isVisible: false } } });
    handleProceedNextPage(null, true)
  }

  function onMinimumLicenYearsPopupNegativeClick() {
    alertModalManager.openModal({ title: t('mobileMinimumYearsLicenceAlertTitle') })
  }

  return (
    <DriverInfoPage isLoading={validateCanSkip}>
      <PageHeader
        title={t("personalInfo")}
        active={1}
        progress={"30%"}
        handleLogoClick={() => props.history.push("/start")}
      />
      <main className="page-layout">
        <div className="container grow">
          <div className="driver-content" style={{ height: "100%" }}>
            <form>
              <LabeledDriverInfoInput
                label={t("name")}
                name="fullName"
                inputRef={registerRef("fullName")}
                autoComplete="off"
                value={state.fullName}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { fullName: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? fullNameValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <DriverDateInput
                t={t}
                label={t("birthDate")}
                validationObject={
                  state.canDisplayErrors
                    ? birthDateValidation
                    : validValidationObject
                }
                inputRef={registerRef("birthDay")}
                onComplete={() => focusNext("birthYear")}
                dayValue={state.birthDay}
                dayName="birthDay"
                monthValue={state.birthMonth}
                monthName="birthMonth"
                yearValue={state.birthYear}
                yearName="birthYear"
                onDayChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { birthDay: value },
                  })
                }
                onMonthChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { birthMonth: value },
                  })
                }
                onYearChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { birthYear: value },
                  })
                }
              />
              <Spacer />
              <DriverInfoDropdown
                selectRef={registerRef("driversLicenseIssuedCountry")}
                name="driversLicenseIssuedCountry"
                label={t("driversLicenseIssuedCountry")}
                placeholder={t("countryHelp")}
                search
                value={state.driversLicenseIssuedPlace}
                options={countriesOptions}
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseIssuedCountryValidation
                    : validValidationObject
                }
                onChange={(value) => {
                  dispatch({
                    type: "SET_VALUES",
                    payload: {
                      driversLicenseIssuedPlace: value,
                    },
                  });
                  focusNext("driversLicenseIssuedCountry");
                }}
              />
              <Spacer />
              <LabeledDriverInfoInput
                label={t("driversLicense")}
                name="driversLicense"
                inputRef={registerRef("driversLicense")}
                autoComplete="off"
                value={state.driversLicense}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicense: value },
                  })
                }
                placeholder={isPortugalCountry(state.driversLicenseIssuedPlace?.label) ? 'Ex: AA-1234567 8' : ''}
                disabled={!state.driversLicenseIssuedPlace || !driversLicenseIssuedCountryValidation.isValid}
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <DriverDateInput
                t={t}
                label={t("driversLicenseIssuedDate")}
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseIssuedDateValidation
                    : validValidationObject
                }
                inputRef={registerRef("driversLicenseIssuedDay")}
                onComplete={() => focusNext("driversLicenseIssuedYear")}
                dayValue={state.driversLicenseIssuedDay}
                dayName="driversLicenseIssuedDay"
                monthValue={state.driversLicenseIssuedMonth}
                monthName="driversLicenseIssuedMonth"
                yearValue={state.driversLicenseIssuedYear}
                yearName="driversLicenseIssuedYear"
                disabled={!state.driversLicenseIssuedPlace || !driversLicenseIssuedCountryValidation.isValid}
                onDayChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseIssuedDay: value },
                  })
                }
                onMonthChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseIssuedMonth: value },
                  })
                }
                onYearChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseIssuedYear: value },
                  })
                }
              />
              <Spacer />
              <DriverDateInput
                t={t}
                label={t("driversLicenseExpirationDate")}
                labelComp={
                  expireDateOptional ? (
                    <span>
                      {t('driversLicenseExpirationDate')} <span className="italic ml-1">{'(' + t('optional') + ')'}</span>
                    </span>
                  ) : (
                    undefined
                  )
                }
                inputRef={registerRef("driversLicenseExpirationDay")}
                dayValue={state.driversLicenseExpirationDay}
                dayName="driversLicenseExpirationDay"
                monthValue={state.driversLicenseExpirationMonth}
                monthName="driversLicenseExpirationMonth"
                yearValue={state.driversLicenseExpirationYear}
                yearName="driversLicenseExpirationYear"
                onDayChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseExpirationDay: value },
                  })
                }
                onMonthChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseExpirationMonth: value },
                  })
                }
                onYearChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { driversLicenseExpirationYear: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? driversLicenseExpirationDateValidation
                    : validValidationObject
                }
                disabled={!state.driversLicenseIssuedPlace || !driversLicenseIssuedCountryValidation.isValid}
              />
            </form>
          </div>
        </div>
        <Footer
          handleNextClick={onNextClick}
          handlePrevClick={onBack}
          nextHighlighted={canNext()}
        />
      </main>
      <AlertModalWithActions
        isOpen={state.minimumLicencePopupInfo.isVisible}
        title={state.minimumLicencePopupInfo.title}
        message={''}
        handleLeftClick={onMinimumLicenYearsPopupNegativeClick}
        leftLabel={t('minimumLicenceRefuse')}
        handleRightClick={onMinimumLicenYearsPopupPositiveClick}
        rightLabel={t('minimumLicenceConfirm')}
      />
    </DriverInfoPage>
  );
}

export default withLang(TrackingPage(AdditionalDriverStepPage, 2007));
